import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  VStack,
  Button,
  List,
  ListItem,
  ListIcon,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { TrendingUp, Target, Users, Cpu, ChevronRight, Rocket, LucideIcon } from 'lucide-react';

const MotionBox = motion(Box);

const VisionCard = ({
  icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) => (
  <Box
    p={{ base: 4, md: 6 }}
    borderRadius='xl'
    bg='rgba(11, 61, 145, 0.3)'
    border='1px solid'
    borderColor='brand.accent'
    transition='all 0.3s'
    _hover={{
      transform: 'translateY(-4px)',
      boxShadow: '0 4px 20px rgba(70, 255, 255, 0.2)',
    }}>
    <Icon as={icon} color='brand.accent' boxSize={{ base: 6, md: 8 }} mb={4} aria-hidden='true' />
    <Text color='brand.accent' fontWeight='bold' mb={2} fontSize={{ base: 'sm', md: 'md' }}>
      {title}
    </Text>
    <Text color='gray.300' fontSize={{ base: 'xs', md: 'sm' }}>
      {description}
    </Text>
  </Box>
);

const Funding = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <Box
      id='funding'
      py={{ base: 12, md: 20 }}
      bg='brand.dark'
      bgGradient='linear(to-b, brand.dark, rgba(11, 61, 145, 0.2))'
      bgImage="linear-gradient(to bottom, rgba(2, 8, 20, 0.95), rgba(2, 8, 20, 0.95)), url('https://images.unsplash.com/photo-1579547945413-497e1b99dac0?auto=format&fit=crop&w=2000&q=80')"
      bgAttachment={{ base: 'scroll', md: 'fixed' }}
      bgSize='cover'
      bgPosition='center'>
      <Container maxW='1200px' px={{ base: 4, md: 6 }}>
        <MotionBox
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}>
          <VStack spacing={{ base: 8, md: 12 }} align='stretch'>
            <VStack spacing={{ base: 3, md: 4 }}>
              <Heading
                as='h2'
                size={{ base: 'lg', md: 'xl' }}
                color='brand.light'
                letterSpacing='wider'
                textAlign='center'>
                Looking for Strategic Partners
              </Heading>
              <Text
                color='gray.300'
                fontSize={{ base: 'md', md: 'lg' }}
                textAlign='center'
                maxW='800px'
                mx='auto'
                px={{ base: 2, md: 0 }}>
                We're seeking visionary partners who share our commitment to transforming developer
                workflows through AI innovation. If you believe in creating more inclusive and
                efficient development tools, we'd love to connect.
              </Text>
            </VStack>

            <Box
              p={{ base: 4, md: 8 }}
              borderRadius='xl'
              bg='rgba(11, 61, 145, 0.3)'
              border='1px solid'
              borderColor='brand.accent'>
              <VStack spacing={{ base: 6, md: 8 }}>
                <Heading as='h3' size={{ base: 'md', md: 'lg' }} color='brand.light'>
                  Our Vision & Goals
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 8 }}>
                  <List spacing={{ base: 2, md: 4 }}>
                    <ListItem
                      color='gray.300'
                      display='flex'
                      alignItems='center'
                      fontSize={{ base: 'sm', md: 'md' }}>
                      <ListIcon as={ChevronRight} color='brand.accent' aria-hidden='true' />
                      <Text>
                        <Text as='span' color='brand.accent' fontWeight='bold'>
                          AI Innovation:{' '}
                        </Text>
                        Developing adaptive AI models that understand different coding styles and
                        workflows
                      </Text>
                    </ListItem>
                    <ListItem
                      color='gray.300'
                      display='flex'
                      alignItems='center'
                      fontSize={{ base: 'sm', md: 'md' }}>
                      <ListIcon as={ChevronRight} color='brand.accent' aria-hidden='true' />
                      <Text>
                        <Text as='span' color='brand.accent' fontWeight='bold'>
                          Inclusive Design:{' '}
                        </Text>
                        Creating tools that adapt to diverse thinking patterns and work preferences
                      </Text>
                    </ListItem>
                  </List>
                  <List spacing={{ base: 2, md: 4 }}>
                    <ListItem
                      color='gray.300'
                      display='flex'
                      alignItems='center'
                      fontSize={{ base: 'sm', md: 'md' }}>
                      <ListIcon as={ChevronRight} color='brand.accent' aria-hidden='true' />
                      <Text>
                        <Text as='span' color='brand.accent' fontWeight='bold'>
                          Market Impact:{' '}
                        </Text>
                        Making development more accessible and efficient for teams worldwide
                      </Text>
                    </ListItem>
                    <ListItem
                      color='gray.300'
                      display='flex'
                      alignItems='center'
                      fontSize={{ base: 'sm', md: 'md' }}>
                      <ListIcon as={ChevronRight} color='brand.accent' aria-hidden='true' />
                      <Text>
                        <Text as='span' color='brand.accent' fontWeight='bold'>
                          Community Growth:{' '}
                        </Text>
                        Building a supportive ecosystem for neurodivergent developers
                      </Text>
                    </ListItem>
                  </List>
                </SimpleGrid>
              </VStack>
            </Box>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={{ base: 4, md: 6 }}>
              <VisionCard
                icon={Users}
                title='Target Market'
                description='Development teams seeking to streamline their workflows and embrace inclusive practices'
              />
              <VisionCard
                icon={Cpu}
                title='Technology'
                description='Advanced AI models designed to understand and adapt to different coding styles'
              />
              <VisionCard
                icon={TrendingUp}
                title='Growth Plan'
                description='Focused on building a strong foundation and expanding through strategic partnerships'
              />
              <VisionCard
                icon={Target}
                title='Mission'
                description='Creating a more inclusive and efficient future for software development'
              />
            </SimpleGrid>

            <Box
              p={{ base: 4, md: 8 }}
              borderRadius='xl'
              bg='rgba(11, 61, 145, 0.3)'
              border='1px solid'
              borderColor='brand.accent'
              className='glow-effect'>
              <Grid
                templateColumns={{ base: '1fr', md: '2fr 1fr' }}
                gap={{ base: 4, md: 8 }}
                alignItems='center'>
                <GridItem>
                  <VStack align='stretch' spacing={{ base: 3, md: 4 }}>
                    <Heading as='h3' size={{ base: 'md', md: 'lg' }} color='brand.light'>
                      Partner With Us
                    </Heading>
                    <Text color='gray.300' fontSize={{ base: 'sm', md: 'md' }}>
                      We're at an exciting stage in our journey, developing innovative AI-powered
                      tools that will reshape how developers work. If you're interested in being
                      part of this transformation, we'd love to share our detailed vision and
                      explore potential collaboration.
                    </Text>
                  </VStack>
                </GridItem>
                <GridItem>
                  <Button
                    variant='primary'
                    size={{ base: 'md', md: 'lg' }}
                    width='full'
                    rightIcon={<Rocket size={20} />}
                    onClick={() => {
                      const element = document.getElementById('contact');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    fontSize={{ base: 'sm', md: 'md' }}
                    height={{ base: '40px', md: 'auto' }}>
                    Start the Conversation
                  </Button>
                </GridItem>
              </Grid>
            </Box>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Funding;
